/* eslint-disable @typescript-eslint/no-this-alias */
import * as Sentry from '@sentry/browser';

import { DEBUG_MODE } from '../spdConfig';

export class Stream {
  protected static deviceCameras: MediaDeviceInfo[] = [];
  protected static currentDeviceIndex = 0; ///index aktualnie używanej kamery
  protected static currentDeviceId: string | null = null; /// identyfikator aktualnie używanej kamery

  public static turnOnCamera(
    video: HTMLVideoElement,
    canplay: () => void,
    deviceInfo: MediaDeviceInfo,
  ) {
    Stream.currentDeviceId = deviceInfo.deviceId;
    DEBUG_MODE &&
      console.log(
        'Aktywowana kamera:',
        deviceInfo,
        'Index:',
        Stream.currentDeviceIndex,
      );
    video.classList.remove('not-available');

    if (
      deviceInfo &&
      (deviceInfo.label.toLowerCase().includes('back') ||
        deviceInfo.label.toLowerCase().includes('rear'))
    ) {
      /// sprawdzam czy to tylnia kamera
      video.classList.add('back-camera');
    } else {
      video.classList.remove('back-camera');
    }

    const attr =
      deviceInfo && deviceInfo.deviceId
        ? { deviceId: { exact: deviceInfo.deviceId } }
        : true;

    navigator.mediaDevices
      .getUserMedia({ video: attr, audio: false })
      .then(async stream => {
        if (video) {
          video.srcObject = stream;
          await video.play();
        }
      })
      .catch(err => {
        console.error(`An error occurred during turnOnCamera: ${err}`);
        Sentry.captureException(err);
        video.classList.add('not-available');
        return;
      });

    video.addEventListener('canplay', canplay, false);
  }

  protected static activateCameras(
    devices: MediaDeviceInfo[],
    video: HTMLVideoElement,
    canplay: () => void,
  ) {
    Stream.deviceCameras = devices.filter(
      device => device.kind === 'videoinput',
    );
    if (Stream.deviceCameras.length > 0) {
      video.classList.remove('not-available');
      if (Stream.deviceCameras.length > 1) {
        video.classList.add('switcher-available');
      } else {
        video.classList.remove('switcher-available');
      }

      DEBUG_MODE && console.log('Aktualna lista kamer:', Stream.deviceCameras);

      const cameraIndex = Stream.deviceCameras.findIndex(
        camera => camera.deviceId === Stream.currentDeviceId,
      );
      if (cameraIndex !== -1) {
        ///nie zmienił się indeks aktywnej kamery, która wciąż jest dostępna lub
        // kamera jest na liście, ale pod innym indeksem, muszę zakutalizować currentDeviceIndex
        Stream.currentDeviceIndex = cameraIndex;
      } else {
        // Kamera już nie istnieje lub jeszcze nie była wybrana, wybierz kamerę pod indeksem 0
        Stream.currentDeviceIndex = 0;
      }
      const device = Stream.deviceCameras[Stream.currentDeviceIndex];
      Stream.currentDeviceId = device.deviceId;
      ///Stream.stopVideo(video); - do zbadania czy to jest potrzebne i jaki ma wpływ na chat
      this.turnOnCamera(video, canplay, device);
    } else {
      Stream.currentDeviceIndex = 0;
      Stream.currentDeviceId = null;
      video.classList.add('not-available');
    }
  }

  public static turnOnDefaultCamera(
    video: HTMLVideoElement,
    canplay: () => void,
  ) {
    navigator.mediaDevices
      .enumerateDevices()
      .then(foundDevices => {
        Stream.activateCameras(foundDevices, video, canplay);
      })
      .catch(err => {
        console.error('An error occurred during turnOnDefaultCamera:', err);
        Sentry.captureException(err);
      });

    navigator.mediaDevices.addEventListener('devicechange', async () => {
      const foundDevices = await navigator.mediaDevices.enumerateDevices();
      Stream.activateCameras(foundDevices, video, canplay);
    });
  }

  public static stopVideo(video: HTMLVideoElement) {
    const mediaStream = video.srcObject as MediaStream;
    Stream.stop(mediaStream);
  }

  public static stop(mediaStream: MediaStream) {
    const tracks = mediaStream && mediaStream.getTracks();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tracks && tracks.forEach((track: { stop: () => any }) => track.stop());
  }

  public static switchCamera(video: HTMLVideoElement, canplay: () => void) {
    if (Stream.deviceCameras.length > 1) {
      Stream.currentDeviceIndex =
        (Stream.currentDeviceIndex + 1) % Stream.deviceCameras.length; // Przełącz na kolejną kamerę
      ///Stream.stopVideo(video);
      const device = Stream.deviceCameras[Stream.currentDeviceIndex];
      Stream.currentDeviceId = device.deviceId;
      this.turnOnCamera(video, canplay, device);
    }
  }
}

export class Scanner {
  // The width and height of the captured photo. We will set the
  // width to the value defined here, but the height will be
  // calculated based on the aspect ratio of the input stream.

  private width = 1280; // We will scale the photo width to this
  private height = 0; // This will be computed based on the input stream

  // |streaming| indicates whether or not we're currently streaming
  // video from the camera. Obviously, we start at false.

  private streaming = false;

  private video: HTMLVideoElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private photo: HTMLImageElement | null = null;
  private defaultEmptyPhoto: string | null = null;

  constructor(id: string, data?: string) {
    const camera = document.getElementById(id);

    if (camera) {
      this.canvas = document.createElement('canvas');
      this.canvas.style.display = 'none';
      camera.appendChild(this.canvas);

      camera.addEventListener('clear', () => this.clearPhoto());
      camera.addEventListener('takePhoto', () => this.takePicture());
      camera.addEventListener('switchCamera', () => this.switchCamera());

      this.photo = camera.querySelector('img.output') as HTMLImageElement;
      this.video = camera.querySelector('video') as HTMLVideoElement;

      Stream.turnOnDefaultCamera(this.video, () => this.streamingOn());

      this.defaultEmptyPhoto = this.getDefaultEmptyPhoto();
      data ? this.setPhoto(data) : this.clearPhoto();
    }
  }

  public getActiveVideo() {
    return this.video;
  }

  protected getDefaultEmptyPhoto() {
    if (this.photo && this.photo.src) {
      return this.photo.src;
    } else {
      if (this.canvas) {
        return Scanner.emptyPhoto(this.canvas.width, this.canvas.height);
      }
    }
    return null;
  }

  protected streamingOn() {
    const that = this;
    if (!that.streaming && that.video && that.canvas) {
      that.height =
        that.video.videoHeight / (that.video.videoWidth / that.width);

      // Firefox currently has a bug where the height can't be read from
      // the video, so we will make assumptions if this happens.

      if (isNaN(that.height)) {
        that.height = that.width / (4 / 3);
      }

      that.video.setAttribute('width', that.width.toString());
      that.video.setAttribute('height', that.height.toString());
      that.streaming = true;
    }
  }

  public static emptyPhoto(width: number, height: number) {
    const blankCanvas = document.createElement('canvas');
    blankCanvas.width = width;
    blankCanvas.height = height;
    const context = blankCanvas.getContext('2d');
    if (context) {
      context.fillStyle = 'grey';
      context.fillRect(0, 0, width, height);
    }
    return blankCanvas.toDataURL('image/png');
  }

  public clearPhoto() {
    if (this.canvas) {
      const context = this.canvas.getContext('2d');
      context && context.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.photo &&
        this.defaultEmptyPhoto &&
        this.photo.setAttribute('src', this.defaultEmptyPhoto);
    }
  }

  protected setPhoto(data: string) {
    const that = this;
    if (this.canvas) {
      const context = this.canvas.getContext('2d');
      if (context && data) {
        const img = new Image();
        img.onload = function () {
          that.canvas &&
            that.canvas.setAttribute('width', img.width.toString());
          that.canvas &&
            that.canvas.setAttribute('height', img.height.toString());
          context.drawImage(img, 0, 0); // offset
        };
        img.src = data;
      }
      this.photo && this.photo.setAttribute('src', data);
    }
  }

  public takePicture() {
    const that = this;
    if (that.canvas && that.video && that.width && that.height) {
      that.canvas.setAttribute('width', that.width.toString());
      that.canvas.setAttribute('height', that.height.toString());

      const context = that.canvas.getContext('2d');
      that.canvas.width = this.width;
      that.canvas.height = this.height;
      context && context.drawImage(that.video, 0, 0, this.width, this.height);

      const data = that.canvas.toDataURL('image/png');
      that.photo && that.photo.setAttribute('src', data);
    } else {
      this.clearPhoto();
    }
  }

  public switchCamera() {
    if (this.video) {
      Stream.switchCamera(this.video, () => this.streamingOn());
    }
  }

  public static clear(id: string) {
    const event = new Event('clear');
    document.getElementById(id)?.dispatchEvent(event);
  }

  public static switchCamera(id: string) {
    const event = new Event('switchCamera');
    document.getElementById(id)?.dispatchEvent(event);
  }

  public static takePhoto(id: string) {
    const event = new Event('takePhoto');
    document.getElementById(id)?.dispatchEvent(event);
  }

  protected static isPhotoEmpty(canvas: HTMLCanvasElement) {
    const blankCanvas = document.createElement('canvas');
    blankCanvas.width = canvas.width;
    blankCanvas.height = canvas.height;
    return canvas.toDataURL() === blankCanvas.toDataURL();
  }

  public static getPhotoAsDataUrl(element: HTMLElement) {
    //const element = document.getElementById(id);
    if (element) {
      const canvas = element.querySelector('canvas') as HTMLCanvasElement;
      return Scanner.isPhotoEmpty(canvas) ? undefined : canvas.toDataURL();
    }
  }

  public static stopActiveCamera(id: string) {
    const element = document.getElementById(id);
    if (element) {
      const video = element.querySelector('video');
      video && Stream.stopVideo(video);
    }
  }
}
